import React, { useState } from "react";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { Delete, Edit, MeetingRoom } from "@mui/icons-material";
import StatusIndicator from "../StatusIndicator/StatusIndicator";
import { EditableField, InlineFieldEdit } from "./EditableField/EditableField";
import {
  MenuBarCardActionFooter,
  MenuBarCardContentBox,
  StyledBodyBox,
  StyledParentBox
} from "./partial/StyledBox.partial";

type P = {
  status: string;
  active: boolean;
  allowForBooking?: boolean;
  disabledSchedule?: any[];
  picturePath?: string;
  title?: string;
  subtitle?: string;
  bottomText?: string;
  onClick: React.MouseEventHandler<HTMLDivElement>;
  onClickEdit?: React.MouseEventHandler;
  onClickDelete?: React.MouseEventHandler;
  /** Should throw if title was not updated successfully */
  onEditTitle?: (value: string) => Promise<any>;
  /** Should throw if subtitle was not updated successfully */
  onEditSubtitle?: (value: string) => Promise<any>;
  onClickOpenExternalDialog?: () => void;
};

const CardItem: React.FC<P> = ({
  status,
  picturePath,
  title,
  subtitle,
  bottomText,
  onClick,
  onClickEdit,
  onClickDelete,
  allowForBooking,
  disabledSchedule,
  onEditTitle,
  onEditSubtitle,
  onClickOpenExternalDialog
}) => {
  const theme = useTheme();

  const [titleField, setTitleField] = useState<InlineFieldEdit>({
    isEditing: false,
    valueBefore: title || "",
    value: title || ""
  });
  const [subField, setSubField] = useState<InlineFieldEdit>({
    isEditing: false,
    valueBefore: subtitle || "",
    value: subtitle || ""
  });
  const isEditingField = titleField.isEditing || subField.isEditing;

  return (
    <StyledParentBox
      picturePath={picturePath}
      onClick={isEditingField ? undefined : onClick}
      data-testid="RootId"
    >
      <MenuBarCardContentBox>
        <StyledBodyBox data-testid="BodyId">
          <Box style={{ maxWidth: "230px" }}>
            <EditableField
              state={titleField}
              setState={setTitleField}
              onEdit={onEditTitle}
              typographySxProps={{
                fontSize: "inherit",
                color: theme.palette.text.primary,
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
              data-testid="NameId"
            />
            <EditableField
              state={subField}
              setState={setSubField}
              typographySxProps={{
                fontSize: "inherit",
                color: theme.palette.text.primary,
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
              onEdit={onEditSubtitle}
              data-testid="subField"
            />
          </Box>
          <StatusIndicator
            allowForBooking={allowForBooking}
            disabledSchedule={disabledSchedule}
            status={status}
          />
        </StyledBodyBox>

        {bottomText ? <Typography>{bottomText}</Typography> : null}
      </MenuBarCardContentBox>

      <MenuBarCardActionFooter>
        {onClickDelete ? (
          <IconButton
            data-testid="onClickDelete-btn"
            aria-label={"clickDelete"}
            size="large"
            onClick={e => {
              onClickDelete(e);
              e.stopPropagation();
            }}
          >
            <Delete />
          </IconButton>
        ) : null}
        {onClickEdit ? (
          <IconButton
            data-testid="onClickEdit-btn"
            aria-label={"clickEdit"}
            size="large"
            onClick={e => {
              onClickEdit(e);
              e.stopPropagation();
            }}
          >
            <Edit />
          </IconButton>
        ) : null}
        {onClickOpenExternalDialog ? (
          <IconButton
            data-testid="onClickOpenExternalDialog-btn"
            size="large"
            onClick={event => {
              event.stopPropagation(); // required so it does not switch to the floor manager
              onClickOpenExternalDialog();
            }}
          >
            <MeetingRoom />
          </IconButton>
        ) : null}
      </MenuBarCardActionFooter>
    </StyledParentBox>
  );
};

export default CardItem;
